package fr.frux.feature.harvests

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.designsystem.format.formatDistance
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapCameraPosition
import fr.frux.feature.harvestdetail.formatHarvestDateTime
import fr.frux.shared.domain.model.NearbyHarvest
import fr.frux.shared.domain.model.Place
import fr.frux.shared.domain.model.SeasonalAround
import fr.frux.shared.domain.model.currentHarvestMonth
import fr.frux.shared.domain.model.seasonalAround
import fr.frux.shared.domain.repository.HarvestRepository
import fr.frux.shared.domain.repository.PlaceRepository
import fr.frux.shared.domain.repository.SpeciesRepository
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * Liste des récoltes à venir autour du centre de la carte (#166, #534).
 *
 * La zone est celle de la carte persistante (ADR 0009), pas la position de l'appareil : c'est là
 * que l'utilisateur regarde et là qu'il crée ses récoltes. Une récolte créée sur Paris restait
 * invisible quand le GPS disait Cantal. Un seul concept de zone dans l'app, et l'onglet marche
 * sans permission de géolocalisation.
 *
 * @param mapCamera positions de la caméra de la carte, fournies par l'assemblage (la feature ne
 *   connaît pas `feature-map`).
 * @param recenterMap déplace la caméra de la carte sur un lieu choisi ici, qui revient ensuite
 *   par [mapCamera] comme n'importe quel déplacement.
 */
class HarvestsViewModel(
    private val harvestRepository: HarvestRepository,
    private val speciesRepository: SpeciesRepository,
    private val placeRepository: PlaceRepository,
    private val mapCamera: Flow<MapCameraPosition>,
    private val recenterMap: (Place) -> Unit,
    private val month: Int = currentHarvestMonth(),
) : ViewModel() {
    private val _state = MutableStateFlow(HarvestsState(isLoading = true))
    val state: StateFlow<HarvestsState> = _state.asStateFlow()

    /** En vol, pour éviter d'accumuler des requêtes concurrentes sur un rechargement rapproché. */
    private var loadJob: Job? = null

    /** Saisie brute du champ de lieu, debouncée en aval — même motif que la carte (#394). */
    private val placeQuery = MutableStateFlow("")

    /** Centre de la dernière géo-query, référence du seuil de rechargement. */
    private var queriedCenter: Location? = null

    /** Dernière position reçue de la carte, avant debounce. */
    private var latestCamera: MapCameraPosition? = null

    /**
     * Lieu choisi dont la caméra n'a pas encore rapporté l'arrivée. Tant qu'il est là, une vue
     * qui sort du debounce loin de lui est antérieure au choix (un pan encore en attente), pas un
     * geste qui l'annule.
     */
    private var awaitedPlace: Place? = null

    init {
        followMapCamera()
        startPlaceSearch()
        viewModelScope.launch {
            speciesRepository.getSpecies().onSuccess { species ->
                // Aucun arbre local ici (#403) : c'est le repli national de [seasonalAround], pas
                // une regionalisation — la carte persistante fournit la version locale quand elle
                // est disponible (cf. FruxEntryProvider).
                _state.update { it.copy(seasonalAround = seasonalAround(species, emptyList(), month)) }
            }
            // Échec silencieux : la rangée éditoriale ne mérite ni erreur ni retry, elle s'absente.
        }
    }

    /**
     * Suit la caméra avec le même repos de geste que la carte, et ne recharge qu'au-delà de
     * [RELOAD_THRESHOLD_METERS] : un petit pan ne doit pas relancer la requête ni faire clignoter
     * la liste. Sur grand écran, l'onglet est à côté de la carte et la suit ainsi en direct.
     */
    @OptIn(FlowPreview::class)
    private fun followMapCamera() {
        viewModelScope.launch {
            mapCamera
                .onEach { latestCamera = it }
                .debounce(CAMERA_DEBOUNCE_MS)
                .collect { camera -> onCameraSettled(camera) }
        }
    }

    private fun onCameraSettled(camera: MapCameraPosition) {
        val center = camera.target
        awaitedPlace?.let { place ->
            if (place.location.distanceMetersTo(center) > RELOAD_THRESHOLD_METERS) return
            awaitedPlace = null
        }
        _state.update { state ->
            // Le lieu choisi ne nomme plus la zone dès que la carte s'en est éloignée.
            val place = state.selectedPlace?.takeIf { it.location.distanceMetersTo(center) <= RELOAD_THRESHOLD_METERS }
            state.copy(selectedPlace = place, mapZoomedOut = camera.zoom < ZOOMED_OUT_BELOW)
        }
        val previous = queriedCenter
        if (previous == null || previous.distanceMetersTo(center) > RELOAD_THRESHOLD_METERS) {
            loadAround(center)
        }
    }

    /**
     * Recherche de lieux (#412) : `mapLatest` annule l'appel en vol dès qu'une frappe arrive.
     * L'échec est silencieux comme sur la carte — un géocodeur en panne ne doit pas couvrir la
     * liste de récoltes d'un message, déplacer la carte reste le chemin normal.
     */
    @OptIn(FlowPreview::class, ExperimentalCoroutinesApi::class)
    private fun startPlaceSearch() {
        viewModelScope.launch {
            placeQuery
                .debounce(PLACE_SEARCH_DEBOUNCE_MS)
                .mapLatest { query ->
                    val trimmed = query.trim()
                    if (trimmed.length < MIN_PLACE_QUERY_LENGTH) return@mapLatest
                    val places = placeRepository.searchPlaces(trimmed).getOrDefault(emptyList())
                    // La saisie a pu repartir pendant l'appel : ne pas écraser un état plus récent.
                    if (placeQuery.value == query) {
                        _state.update { it.copy(placeResults = places) }
                    }
                }.collect {}
        }
    }

    /**
     * La saisie est exposée immédiatement (le champ doit suivre la frappe), l'appel part du
     * debounce. Une requête trop courte vide les résultats sans attendre : effacer sa recherche
     * doit nettoyer l'écran tout de suite.
     */
    fun onPlaceQueryChange(query: String) {
        _state.update {
            it.copy(
                placeQuery = query,
                placeResults = if (query.trim().length < MIN_PLACE_QUERY_LENGTH) emptyList() else it.placeResults,
            )
        }
        placeQuery.value = query
    }

    /**
     * Choisir un lieu recentre la carte (ADR 0024) et la liste le suit. Le chargement part d'ici
     * sans attendre le retour de la caméra : la carte cachée derrière l'onglet peut tarder à
     * rapporter son déplacement, et le retour, une fois là, tombe sous le seuil.
     */
    fun selectPlace(place: Place) {
        placeQuery.value = ""
        // Carte déjà sur place : aucun déplacement ne reviendra confirmer l'arrivée.
        val alreadyThere =
            latestCamera?.target?.let { place.location.distanceMetersTo(it) <= RELOAD_THRESHOLD_METERS } == true
        awaitedPlace = place.takeUnless { alreadyThere }
        _state.update { it.copy(selectedPlace = place, placeQuery = "", placeResults = emptyList()) }
        recenterMap(place)
        loadAround(place.location)
    }

    /** Oublier le lieu : la carte reste où elle est, seul le libellé de zone change. */
    fun clearSelectedPlace() {
        placeQuery.value = ""
        _state.update { it.copy(selectedPlace = null, placeQuery = "", placeResults = emptyList()) }
    }

    /** Relance la géo-query sur la zone courante (bouton « Réessayer »). */
    fun load() {
        queriedCenter?.let(::loadAround)
    }

    private fun loadAround(center: Location) {
        queriedCenter = center
        loadJob?.cancel()
        loadJob =
            viewModelScope.launch {
                _state.update { it.copy(isLoading = true, error = null) }
                harvestRepository
                    .getNearbyHarvests(
                        lat = center.latitude,
                        lon = center.longitude,
                        radius = NEARBY_RADIUS_KM,
                    ).collect { outcome ->
                        outcome.fold(
                            onSuccess = { nearby ->
                                _state.update {
                                    it.copy(
                                        harvests = nearby.harvests.map { harvest -> harvest.toItem() },
                                        isLoading = false,
                                        error = null,
                                    )
                                }
                            },
                            onFailure = { error ->
                                _state.update {
                                    it.copy(isLoading = false, error = error.asAppError())
                                }
                            },
                        )
                    }
            }
    }

    private fun NearbyHarvest.toItem(): HarvestListItem {
        val distanceText = "à ${formatDistance(distanceMeters)}"
        return HarvestListItem(
            id = id,
            title = description?.takeIf { it.isNotBlank() } ?: "Récolte du ${formatHarvestDateTime(scheduledDate)}",
            meta = "${tree?.speciesCommonName ?: site?.name.orEmpty()} · $distanceText",
            scheduledDate = scheduledDate,
            participantsCount = participantsCount,
            maxParticipants = maxParticipants,
            isFull = isFull,
            hostedByOwner = hostedByOwner,
        )
    }

    companion object {
        /**
         * Rayon fixe autour du centre de la carte, quel que soit le zoom : dézoomée sur la France,
         * la liste resterait illisible et la requête sans borne.
         */
        internal const val NEARBY_RADIUS_KM = 25.0

        /** Dix pour cent du rayon : en dessous, la liste ne changerait presque pas. */
        internal const val RELOAD_THRESHOLD_METERS = NEARBY_RADIUS_KM * 1_000 * 0.1

        /** Même repos de geste que la carte (`CAMERA_DEBOUNCE_MS` de `feature-map`). */
        internal const val CAMERA_DEBOUNCE_MS = 400L

        /**
         * En dessous de ce zoom, un écran de téléphone couvre plus de 25 km de rayon : la liste
         * le dit, sinon elle semblerait représenter toute la vue.
         */
        internal const val ZOOMED_OUT_BELOW = 10f

        /** Même valeur que la carte (#53/#394) : la frappe doit se poser avant tout appel réseau. */
        internal const val PLACE_SEARCH_DEBOUNCE_MS = 300L

        /** En dessous, le géocodeur rend du bruit : deux lettres est le plancher retenu (#394). */
        internal const val MIN_PLACE_QUERY_LENGTH = 2
    }
}

data class HarvestsState(
    val harvests: List<HarvestListItem> = emptyList(),
    val isLoading: Boolean = false,
    /** Erreur bloquante de la géo-query, résolue par l'écran (#176). */
    val error: AppError? = null,
    /** Carte assez dézoomée pour que la vue dépasse le rayon de la liste (#534). */
    val mapZoomedOut: Boolean = false,
    /**
     * Repli national de la mise en avant « de saison » (#166/#403, ADR 0020), vide si le
     * référentiel est indisponible. Sert quand la carte persistante n'a pas encore d'arbres
     * chargés à croiser (cf. FruxEntryProvider, qui préfère sa propre [SeasonalAround] locale).
     */
    val seasonalAround: SeasonalAround = SeasonalAround(emptyList(), emptyList(), isLocal = false),
    /** Saisie du champ de lieu (#412), vidée dès qu'un lieu est choisi. */
    val placeQuery: String = "",
    /** Lieux proposés par le géocodeur pour la saisie courante. */
    val placeResults: List<Place> = emptyList(),
    /** Lieu choisi : il nomme la zone tant que la carte ne s'en éloigne pas. */
    val selectedPlace: Place? = null,
)

data class HarvestListItem(
    val id: Long,
    val title: String,
    val meta: String,
    val scheduledDate: Long,
    val participantsCount: Int,
    val maxParticipants: Int?,
    val isFull: Boolean,
    /** Récolte accueillie par le propriétaire du lieu (ADR 0035) : pastille « Chez moi ». */
    val hostedByOwner: Boolean = false,
)
